import {useMessage} from '@coolcolduk/ui-mui';
import {mapPromiseFnSeries} from '@coolcolduk/util';
import {ContentCopy, ExpandMore} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {useCallback, useState} from 'react';
import {SeedQuestion} from '@cp/model';
import {useGenerateQuestionWithSeed} from '../../hooks/generate/useGenerateQuestionWithSeed';
import {useGenerateQuestionWithSeedPrompts} from '../../hooks/generate/useGenerateQuestionWithSeedPrompts';

export interface SeedQuestionVariantImportError {
  line: number;
  message: string;
}

export interface SeedQuestionVariantImportResult {
  successCount: number;
  errorCount: number;
  errors: SeedQuestionVariantImportError[];
}

export interface SeedQuestionVariantImportProgress {
  completed: number;
  total: number;
  successCount: number;
  errorCount: number;
}

export interface GenerateSeedQuestionVariantPromptDialogProps {
  open: boolean;
  seedQuestion: SeedQuestion;
  moduleId: string;
  onClose: () => void;
  /** Import sends each line of the textbox to this function (parsing is done on the backend). */
  onCreateQuestionFromString: (questionJsonString: string) => Promise<void>;
}

export default function GenerateSeedQuestionVariantPromptDialog({
  open,
  seedQuestion,
  moduleId,
  onClose,
  onCreateQuestionFromString,
}: GenerateSeedQuestionVariantPromptDialogProps) {
  const {showMessage} = useMessage();
  const {generateQuestionWithSeedPrompts, loading} = useGenerateQuestionWithSeedPrompts();
  const {generateQuestionWithSeed, loading: generateLoading} = useGenerateQuestionWithSeed();
  const [numberOfVariants, setNumberOfVariants] = useState<number>(5);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPrompt, setUserPrompt] = useState('');
  const [aiOutput, setAiOutput] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState<SeedQuestionVariantImportProgress | null>(null);
  const [importErrors, setImportErrors] = useState<SeedQuestionVariantImportError[]>([]);
  const [showImportLog, setShowImportLog] = useState(false);

  const handleCreatePrompt = useCallback(async () => {
    if (!numberOfVariants || numberOfVariants < 1) {
      showMessage('Please enter a valid number of variants (at least 1)', 6000, 'error');
      return;
    }

    try {
      const prompts = await generateQuestionWithSeedPrompts({
        moduleId,
        seedQuestionId: seedQuestion.id,
        numberOfVariants,
      });

      setSystemPrompt(prompts.system);
      setUserPrompt(prompts.user);
    } catch (error) {
      console.error('Error generating prompts:', error);
      showMessage('Failed to generate prompts. Please try again.', 6000, 'error');
    }
  }, [moduleId, seedQuestion.id, numberOfVariants, generateQuestionWithSeedPrompts, showMessage]);

  const handleGenerateQuestions = useCallback(async () => {
    if (!numberOfVariants || numberOfVariants < 1) {
      showMessage('Please enter a valid number of variants (at least 1)', 6000, 'error');
      return;
    }

    try {
      const jsonlLines = await generateQuestionWithSeed({
        moduleId,
        seedQuestionId: seedQuestion.id,
        numberOfVariants,
        additionalInfo: additionalInfo.trim() || undefined,
      });

      // Join the JSONL lines with newlines and set in the textbox
      setAiOutput(jsonlLines.join('\n'));
      showMessage(`Generated ${jsonlLines.length} question variants`, 3000, 'success');
    } catch (error) {
      console.error('Error generating questions:', error);
      showMessage('Failed to generate questions. Please try again.', 6000, 'error');
    }
  }, [moduleId, seedQuestion.id, numberOfVariants, additionalInfo, generateQuestionWithSeed, showMessage]);

  const handleCopySystemPrompt = useCallback(() => {
    navigator.clipboard.writeText(systemPrompt);
    showMessage('System prompt copied to clipboard', 3000, 'success');
  }, [systemPrompt, showMessage]);

  const handleCopyUserPrompt = useCallback(() => {
    navigator.clipboard.writeText(userPrompt);
    showMessage('User prompt copied to clipboard', 3000, 'success');
  }, [userPrompt, showMessage]);

  const handleAiOutputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAiOutput(e.target.value);
  }, []);

  const handleToggleImportLog = useCallback(() => {
    setShowImportLog((prev) => !prev);
  }, []);

  const handleApply = useCallback(async () => {
    if (!aiOutput.trim()) {
      showMessage('Please paste the AI-generated output', 6000, 'error');
      return;
    }

    const lines = aiOutput
      .trim()
      .split('}\n') // split by } and then \n due to JSONL
      .map((line) => {
        const l = line.trim();
        return l.at(-1) === '}' ? l : l + '}';
      })
      .filter(Boolean);

    if (lines.length === 0) {
      showMessage('No lines to import', 6000, 'error');
      return;
    }

    setImportLoading(true);
    setImportErrors([]);
    setShowImportLog(false);
    setImportProgress({completed: 0, total: lines.length, successCount: 0, errorCount: 0});

    const errors: SeedQuestionVariantImportError[] = [];
    let successCount = 0;
    let errorCount = 0;

    const updateProgress = () =>
      setImportProgress({completed: successCount + errorCount, total: lines.length, successCount, errorCount});

    try {
      await mapPromiseFnSeries(lines, async (line, index) => {
        const lineNumber = index + 1;
        try {
          await onCreateQuestionFromString(line);
          successCount++;
        } catch (createError) {
          errors.push({
            line: lineNumber,
            message: createError instanceof Error ? createError.message : 'Create failed',
          });
          errorCount++;
        }
        updateProgress();
      });

      setImportLoading(false);
      setImportErrors(errors);

      if (errorCount === 0) {
        onClose();
        showMessage(`${successCount} question variants imported successfully!`, 6000, 'success');
      }
    } catch (error) {
      console.error('Error during import:', error);
      setImportLoading(false);
      showMessage(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 6000, 'error');
    }
  }, [aiOutput, onCreateQuestionFromString, onClose, showMessage]);

  const handleClose = useCallback(() => {
    setImportLoading(false);
    setImportProgress(null);
    setImportErrors([]);
    setShowImportLog(false);
    setSystemPrompt('');
    setUserPrompt('');
    setAiOutput('');
    setAdditionalInfo('');
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Generate Question Variants from Seed Question</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{mt: 1}}>
          <TextField
            label="Number of Variants"
            type="number"
            value={numberOfVariants}
            onChange={(e) => setNumberOfVariants(Number.parseInt(e.target.value) || 1)}
            inputProps={{min: 1, max: 20}}
            fullWidth
          />

          <TextField
            label="Additional Info (Optional)"
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(e.target.value)}
            multiline
            rows={2}
            fullWidth
            placeholder="Any additional instructions for generating variants..."
          />

          <Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={handleCreatePrompt}
                disabled={loading || !numberOfVariants}
                sx={{
                  bgcolor: '#1B263B',
                  '&:hover': {bgcolor: '#1B263B', opacity: 0.8},
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Generate Prompts'}
              </Button>
              <Button
                variant="contained"
                onClick={handleGenerateQuestions}
                disabled={generateLoading || !numberOfVariants}
                sx={{
                  bgcolor: '#FF6F59',
                  '&:hover': {bgcolor: '#FF6F59', opacity: 0.8},
                }}
              >
                {generateLoading ? <CircularProgress size={20} color="inherit" /> : 'Generate Questions'}
              </Button>
            </Stack>
          </Box>

          {systemPrompt && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{width: '100%'}}>
                  <Typography variant="subtitle2" sx={{color: '#1B263B'}}>
                    System Prompt
                  </Typography>
                  <Tooltip title="Copy to clipboard">
                    <IconButton
                      component="span"
                      size="small"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopySystemPrompt();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCopySystemPrompt();
                        }
                      }}
                      onFocus={(e) => e.stopPropagation()}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  value={systemPrompt}
                  multiline
                  rows={8}
                  fullWidth
                  InputProps={{readOnly: true}}
                  sx={{bgcolor: '#f5f5f5'}}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {userPrompt && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{width: '100%'}}>
                  <Typography variant="subtitle2" sx={{color: '#1B263B'}}>
                    User Prompt
                  </Typography>
                  <Tooltip title="Copy to clipboard">
                    <IconButton
                      component="span"
                      size="small"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyUserPrompt();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCopyUserPrompt();
                        }
                      }}
                      onFocus={(e) => e.stopPropagation()}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  value={userPrompt}
                  multiline
                  rows={6}
                  fullWidth
                  InputProps={{readOnly: true}}
                  sx={{bgcolor: '#f5f5f5'}}
                />
              </AccordionDetails>
            </Accordion>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{color: '#1B263B', mb: 1}}>
              Paste AI-Generated Output (JSONL)
            </Typography>
            <TextField
              value={aiOutput}
              onChange={handleAiOutputChange}
              multiline
              rows={10}
              fullWidth
              placeholder="Paste the AI-generated JSON array of question variants here..."
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Box sx={{width: '100%'}}>
          {importProgress && (
            <Alert
              severity={importProgress.errorCount > 0 ? 'warning' : 'info'}
              sx={{mb: 1}}
              action={
                importProgress.errorCount > 0 ? (
                  <Button size="small" onClick={handleToggleImportLog}>
                    {showImportLog ? 'Hide log' : 'View log'}
                  </Button>
                ) : undefined
              }
            >
              {importProgress.completed} of {importProgress.total} completed
              {importProgress.errorCount > 0
                ? ` (${importProgress.successCount} successful, ${importProgress.errorCount} failed)`
                : ` (${importProgress.successCount} successful)`}
            </Alert>
          )}

          <Collapse in={showImportLog && importErrors.length > 0} unmountOnExit>
            <Box sx={{mb: 1, px: 1}}>
              <Typography variant="subtitle2" sx={{mb: 0.5}}>
                Import errors
              </Typography>
              <Stack spacing={0.5}>
                {importErrors.map((err) => (
                  <Typography key={`${err.line}-${err.message}`} variant="body2" sx={{fontFamily: 'monospace'}}>
                    Line {err.line}: {err.message}
                  </Typography>
                ))}
              </Stack>
            </Box>
          </Collapse>

          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={handleClose} disabled={importLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              variant="contained"
              disabled={!aiOutput.trim() || importLoading}
              sx={{
                bgcolor: '#FF6F59',
                '&:hover': {bgcolor: '#FF6F59', opacity: 0.8},
              }}
            >
              {importLoading ? <CircularProgress size={20} color="inherit" /> : 'Import Questions'}
            </Button>
          </Stack>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
